import { Activity } from 'lucide-react';
import { useState } from 'react';
import {
  type HealthAlertConfig,
  type HealthScoreData,
  useHealthAlertConfig,
  useHealthScore,
  useSendHealthAlert,
  useUpdateHealthAlertConfig,
} from '../hooks/useAdminData';
import { Badge } from './Badge';

type Grade = HealthScoreData['status'];

function gradeBadge(status: Grade): 'success' | 'warning' | 'danger' {
  return status === 'healthy' ? 'success' : status === 'degraded' ? 'warning' : 'danger';
}

function barColor(status: Grade): string {
  return status === 'healthy' ? 'bg-success' : status === 'degraded' ? 'bg-warning' : 'bg-danger';
}

export function HealthScorePanel({ days }: { days: number }) {
  const { data: score, isLoading } = useHealthScore(days);
  const { data: config } = useHealthAlertConfig();
  const updateConfig = useUpdateHealthAlertConfig();
  const sendAlert = useSendHealthAlert();

  const [recipients, setRecipients] = useState('');
  const [slackWebhookUrl, setSlackWebhookUrl] = useState('');
  const [sendResult, setSendResult] = useState<string | null>(null);
  const [syncedFrom, setSyncedFrom] = useState<HealthAlertConfig | undefined>(undefined);

  // Seed the form from the saved config once it loads (and if it changes),
  // adjusting state during render rather than in an effect.
  if (config && config !== syncedFrom) {
    setSyncedFrom(config);
    setRecipients(config.recipients.join(', '));
    setSlackWebhookUrl(config.slackWebhookUrl ?? '');
  }

  const handleSaveConfig = () => {
    updateConfig.mutate({
      recipients: recipients
        .split(',')
        .map((r) => r.trim())
        .filter(Boolean),
      slackWebhookUrl: slackWebhookUrl.trim() || undefined,
    });
  };

  const handleSendTest = async () => {
    setSendResult(null);
    const result = await sendAlert.mutateAsync({ force: true, days });
    setSendResult(
      result.dispatched
        ? `Sent — Slack: ${result.channels.slack}, Email: ${result.channels.email}`
        : `Not sent: ${result.reason ?? 'platform healthy'}`,
    );
  };

  return (
    <div className="bg-surface rounded-xl border border-border p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Activity size={18} className="text-primary" />
          <h2 className="text-lg font-semibold text-text">Health Score</h2>
        </div>
        {score && (
          <div className="flex items-center gap-2">
            <span className="text-2xl font-semibold text-text">{score.overall}</span>
            <span className="text-sm text-text-muted">/100</span>
            <Badge variant={gradeBadge(score.status)}>{score.status}</Badge>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="text-sm text-text-muted">Computing score…</div>
      ) : (
        <div className="space-y-2 mb-5">
          {score?.domains.map((d) => (
            <div key={d.domain} className="flex items-center gap-3">
              <span className="text-xs text-text-muted w-24 capitalize">{d.domain}</span>
              <div className="flex-1 bg-background rounded-full h-4 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${barColor(d.status)}`}
                  style={{ width: `${Math.max(d.score, 2)}%` }}
                />
              </div>
              <span className="text-xs text-text-secondary w-12 text-right">{d.score}</span>
              <span className="text-xs text-text-muted w-44 truncate" title={d.detail}>
                {d.detail}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="border-t border-border pt-4 space-y-3">
        <h3 className="text-xs font-medium text-text-muted">Alert recipients (super admins)</h3>
        <input
          type="text"
          value={recipients}
          onChange={(e) => setRecipients(e.target.value)}
          placeholder="comma-separated emails"
          aria-label="Alert email recipients"
          className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:border-primary"
        />
        <input
          type="text"
          value={slackWebhookUrl}
          onChange={(e) => setSlackWebhookUrl(e.target.value)}
          placeholder="Slack webhook URL (optional)"
          aria-label="Slack webhook URL"
          className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:border-primary"
        />
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleSaveConfig}
            disabled={updateConfig.isPending}
            className="px-3 py-2 bg-primary text-white rounded-lg text-sm font-medium disabled:opacity-50 hover:opacity-90 transition-opacity"
          >
            {updateConfig.isPending ? 'Saving…' : 'Save config'}
          </button>
          <button
            type="button"
            onClick={handleSendTest}
            disabled={sendAlert.isPending}
            className="px-3 py-2 bg-surface border border-border text-text rounded-lg text-sm font-medium disabled:opacity-50 hover:border-primary transition-colors"
          >
            {sendAlert.isPending ? 'Sending…' : 'Send test alert'}
          </button>
          {sendResult && <span className="text-xs text-text-secondary">{sendResult}</span>}
        </div>
      </div>
    </div>
  );
}
